import { type IDictionary } from '@/types/common';
import {
  type SessionFeedbackParams,
  SessionFeedbackType,
} from '@/types/session';

export const RELOAD_CAL_MODAL = 1500;

export const RECURRING_SESSION_COUNT = 1500;

export const SESSION_FEEDBACK_GRAPH_SIZE = 30;

// MEN-4928: the Sessions page is a single long scroll with no pagination
// control, so `getAllSessions` walks every server page and hands back the
// merged set. This is the per-request page size for that walk — large enough
// that the common viewer is served in one round trip, not a cap on how many
// sessions can be shown.
export const SESSIONS_FETCH_PAGE_SIZE = 500;

// Backstop on the page walk so a server that misreports `pages` cannot spin
// the client forever. At the page size above this is 25,000 sessions — far
// beyond any real member or coach history, so it should never bind. When it
// does bind, `getAllSessions` reports the unfetched remainder through `pages`
// rather than presenting a truncated list as complete.
export const MAX_SESSIONS_FETCH_PAGES = 50;

// How many session rows the long scroll mounts at a time. `SessionList` has no
// virtualization, so the complete list is revealed a window at a time as the
// reader scrolls rather than all at once.
export const SESSIONS_RENDER_STEP = 50;

export const SESSION_FEEDBACK_TYPES: IDictionary<SessionFeedbackParams> = {
  mixed: {
    background: 'bg-sunny-100 hover:bg-sunny-200',
    border: 'border-solid border-2 border-sunny-500',
    img: 'https://mento-space.nyc3.cdn.digitaloceanspaces.com/assets/okay-face.png',
    name: 'Mixed',
    statsGraphColor: 'bg-sunny-500',
    subtitle: 'How can we improve your experience?',
    title: 'Thank you for sharing.',
    type: SessionFeedbackType.MIXED,
  },
  negative: {
    background: 'bg-poppy-100 hover:bg-poppy-200',
    border: 'border-solid border-2 border-poppy-500',
    img: 'https://mento-space.nyc3.digitaloceanspaces.com/assets/icon-vote-negative.svg',
    name: 'Negative',
    statsGraphColor: 'bg-poppy-500',
    subtitle: 'How can we improve your experience?',
    title: 'We are sorry to hear that.',
    type: SessionFeedbackType.NEGATIVE,
  },
  positive: {
    background: 'bg-sage-100 hover:bg-sage-200',
    border: 'border-solid border-2 border-sage-500',
    img: 'https://mento-space.nyc3.digitaloceanspaces.com/assets/icon-vote-positive.svg',
    name: 'Positive',
    statsGraphColor: 'bg-[#88d38c]',
    subtitle: 'What is one way your coach had a positive impact on you today?',
    title: 'Awesome to hear!',
    type: SessionFeedbackType.POSITIVE,
  },
};

export enum SessionCancelChoice {
  PENDING = 'PENDING',
  RECURRING = 'RECURRING',
  RESCHEDULE = 'RESCHEDULE',
  RESCHEDULE_PROMPT = 'RESCHEDULE_PROMPT',
  SINGLE = 'SINGLE',
}

export enum SessionAction {
  CANCEL = 'cancel',
  GROWTH = 'growth',
  RESCHEDULE = 'reschedule',
  VIEW = 'view',
}
